/* =====================================================================
   Real Automation — Referral System (Quest 3)
   Shared frontend helper. Loaded by every ra_referral_* section.
   Talks ONLY to the Edge Function (the gateway). No auth headers — tokens
   ride in the JSON body.

   AUTH MODEL:
     • access_token  = short-lived (15 min), sent on private calls as body.token
     • refresh_token = long-lived, used to mint a new access token
   On signup/login we store both. Private calls auto-refresh once on 401.

   The EF base URL is injected by the sections via:
     window.RA_REFERRAL_EF = "https://<project>.functions.supabase.co/referral-gateway";
   ===================================================================== */
(function () {
  if (window.RAReferral) return; // load-once guard

  var AT_KEY   = "ra_ref_at";    // access token
  var RT_KEY   = "ra_ref_rt";    // refresh token
  var USER_KEY = "ra_ref_user";

  function efUrl() {
    var u = window.RA_REFERRAL_EF;
    if (!u) console.warn("[RAReferral] window.RA_REFERRAL_EF is not set");
    return u;
  }

  function ls(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function lsDel(k) { try { localStorage.removeItem(k); } catch (e) {} }

  function getAccess()  { return ls(AT_KEY); }
  function getRefresh() { return ls(RT_KEY); }
  function getUser()    { try { return JSON.parse(ls(USER_KEY) || "null"); } catch (e) { return null; } }
  function storeAuth(res) {
    if (res.access_token)  lsSet(AT_KEY, res.access_token);
    if (res.refresh_token) lsSet(RT_KEY, res.refresh_token);
    if (res.user)          lsSet(USER_KEY, JSON.stringify(res.user));
  }
  function clearAuth() { lsDel(AT_KEY); lsDel(RT_KEY); lsDel(USER_KEY); }

  // Low-level POST. Returns the parsed body, throws {message,status} on error.
  function rawPost(payload) {
    return fetch(efUrl(), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }).then(function (r) {
      return r.json().catch(function () { return { error: "bad_response" }; })
        .then(function (body) {
          if (!r.ok || (body && body.error)) {
            var err = new Error((body && body.error) || "request_failed");
            err.status = r.status; err.body = body;
            throw err;
          }
          return body;
        });
    });
  }

  function post(type, data) { return rawPost({ type: type, data: data || {} }); }

  // Exchange the refresh token for a new access (+ rotated refresh) token.
  function refresh() {
    var rt = getRefresh();
    if (!rt) return Promise.reject(new Error("no_refresh"));
    return rawPost({ type: "referral_refresh", data: { refresh_token: rt } })
      .then(function (res) { storeAuth(res); return res; })
      .catch(function (err) { clearAuth(); throw err; });
  }

  // Private call: attach the access token; on 401 refresh once and retry.
  function postPrivate(type, data) {
    var at = getAccess();
    function attempt(token) { return rawPost({ type: type, data: data || {}, token: token }); }
    var first = at ? attempt(at) : Promise.reject(new Error("unauthorized"));
    return first.catch(function (err) {
      if (err && (err.message === "unauthorized" || err.status === 401)) {
        return refresh().then(function (res) { return attempt(res.access_token); });
      }
      throw err;
    });
  }

  // Read ?ref=CODE; fall back to a stored value so it survives navigation.
  function getRef() {
    var p = new URLSearchParams(window.location.search);
    var ref = p.get("ref");
    if (ref) { try { sessionStorage.setItem("ra_ref_code", ref); } catch (e) {} return ref; }
    try { return sessionStorage.getItem("ra_ref_code"); } catch (e) { return null; }
  }

  // Stamp ?ref=CODE onto same-origin links so the code rides the URL into the
  // next page (survives new tabs / shared links, not just same-tab sessionStorage).
  function keepRef(selector) {
    var ref = getRef();
    if (!ref) return;
    var links = document.querySelectorAll(selector || "a[href]");
    Array.prototype.forEach.call(links, function (a) {
      var href = a.getAttribute("href");
      if (!href) return;
      if (/^(#|mailto:|tel:|javascript:)/i.test(href)) return;
      try {
        var url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin) return;   // same-origin only
        if (url.searchParams.get("ref")) return;             // don't clobber
        url.searchParams.set("ref", ref);
        a.setAttribute("href", url.pathname + url.search + url.hash);
      } catch (e) {}
    });
  }

  window.RAReferral = {
    // ---- auth ----
    signup: function (d) { return post("referral_signup", d).then(function (res) { storeAuth(res); return res; }); },
    login:  function (d) { return post("referral_login", d).then(function (res) { storeAuth(res); return res; }); },
    refresh: refresh,
    logout: function () {
      var rt = getRefresh();
      return rawPost({ type: "referral_logout", data: { refresh_token: rt } })
        .catch(function () { /* revoke best-effort */ })
        .then(function () { clearAuth(); });
    },

    // ---- dashboard (private) ----
    getDashboard: function () { return postPrivate("referral_get_dashboard", {}); },
    // Affiliate submits a business/lead for us to review before meeting.
    submitBusinessReferral: function (d) { return postPrivate("referral_business", d || {}); },

    // ---- landing page (public) ----
    trackOpen: function (ref) {
      return post("referral_track_open", {
        ref: ref || getRef(),
        user_agent: navigator.userAgent,
        referer: document.referrer || "",
      }).catch(function () { /* fire-and-forget */ });
    },
    submitConversion: function (d, opts) {
      opts = opts || {};
      d = d || {}; d.ref = d.ref || getRef();
      if (opts.dedupe !== false && this.hasConverted()) {
        return Promise.resolve({ ok: true, deduped: true });
      }
      return post("referral_submit_conversion", d).then(function (res) {
        try { sessionStorage.setItem("ra_ref_converted", "1"); } catch (e) {}
        return res;
      });
    },
    hasConverted: function () {
      try { return sessionStorage.getItem("ra_ref_converted") === "1"; } catch (e) { return false; }
    },
    trackOpenOnce: function () {
      var ref = getRef();
      if (!ref) return;
      try {
        if (sessionStorage.getItem("ra_ref_opened") === "1") return;
        sessionStorage.setItem("ra_ref_opened", "1");
      } catch (e) {}
      this.trackOpen(ref);
    },
    bindConversions: function (opts) {
      opts = opts || {};
      var self = this;
      var ref = getRef();
      var nodes = document.querySelectorAll("[data-ra-convert]");
      Array.prototype.forEach.call(nodes, function (el) {
        if (el.__raBound) return; el.__raBound = true;
        el.addEventListener("click", function () {
          var source = el.getAttribute("data-ra-source") || (el.textContent || "cta").trim().slice(0, 40);
          self.submitConversion({ ref: ref, source: source }, { dedupe: opts.dedupe !== false });
        });
      });
    },

    // ---- utils ----
    getRef: getRef,
    keepRef: keepRef,
    getUser: getUser,
    // Wipe local tokens WITHOUT calling the server. Used to break the
    // auth<->dashboard bounce when a stored token is rejected.
    clearLocal: clearAuth,
    // "logged in enough to try": we have a refresh token (access may be expired).
    isLoggedIn: function () { return !!getRefresh(); },
  };
})();
